"use client";

import React, { useState, useRef } from "react";
import Image from "next/image";
import { FadeIn } from "@/app/components/FadeIn";

interface MarketImageGalleryProps {
  title: string;
  thumbnailUrl: string | null;
  previewImages: string[];
}

export default function MarketImageGallery({
  title,
  thumbnailUrl,
  previewImages,
}: MarketImageGalleryProps) {
  // Use a Set to ensure only UNIQUE images are shown
  const allImages = Array.from(new Set([
    ...(thumbnailUrl ? [thumbnailUrl] : []),
    ...previewImages,
  ].filter(Boolean)));
  
  const [activeImage, setActiveImage] = useState(allImages[0] || null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const { scrollLeft, clientWidth } = scrollContainerRef.current;
      const scrollTo = direction === "left" 
        ? scrollLeft - clientWidth / 2 
        : scrollLeft + clientWidth / 2;
      
      scrollContainerRef.current.scrollTo({
        left: scrollTo,
        behavior: "smooth"
      });
    }
  };

  if (allImages.length === 0) {
    return (
      <FadeIn direction="up">
        <div className="border border-outline-variant bg-surface-container-low aspect-video overflow-hidden relative group rounded-xl">
          <div className="w-full h-full flex items-center justify-center">
            <span className="material-symbols-outlined text-[64px] text-secondary/20">image</span>
          </div>
        </div>
      </FadeIn>
    );
  }

  return (
    <div className="space-y-12">
      {/* Main Display */}
      <FadeIn direction="up">
        <div className="border border-outline-variant bg-surface-container-low aspect-video overflow-hidden relative group rounded-xl shadow-sm">
          {activeImage ? (
            <Image
              src={activeImage}
              alt={title}
              fill
              sizes="(max-width: 1024px) 100vw, 67vw"
              priority
              className="object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
              key={activeImage}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-secondary/20">No preview</div>
          )}
          <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
        </div>
      </FadeIn>

      {/* Thumbnails with Horizontal Navigation */}
      {allImages.length > 1 && (
        <FadeIn delay={0.2}>
          <div className="relative group/gallery">
            {/* Scroll Buttons */}
            <button 
              onClick={() => scroll("left")}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-white border border-outline-variant w-10 h-10 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/gallery:opacity-100 transition-opacity hover:border-primary active:scale-90"
              aria-label="Scroll left"
            >
              <span className="material-symbols-outlined text-primary">chevron_left</span>
            </button>

            <button 
              onClick={() => scroll("right")}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-white border border-outline-variant w-10 h-10 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/gallery:opacity-100 transition-opacity hover:border-primary active:scale-90"
              aria-label="Scroll right"
            >
              <span className="material-symbols-outlined text-primary">chevron_right</span>
            </button>

            {/* Thumbnail Track */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto no-scrollbar scroll-smooth pb-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(img)}
                  className={`flex-shrink-0 border bg-surface aspect-square w-32 md:w-40 cursor-pointer overflow-hidden transition-all duration-300 group/thumb rounded-lg ${
                    activeImage === img
                      ? "border-2 border-primary ring-2 ring-primary/10"
                      : "border-outline-variant hover:border-primary"
                  }`}
                >
                  <Image
                    src={img}
                    alt={`${title} Preview ${idx + 1}`}
                    width={200}
                    height={200}
                    className={`w-full h-full object-cover transition-all duration-700 ease-in-out group-hover/thumb:scale-110 ${
                      activeImage === img ? "grayscale-0" : "grayscale group-hover/thumb:grayscale-0"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </FadeIn>
      )}
    </div>
  );
}
